import React, { createContext, useContext, useRef, ReactNode, useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { SWAGGER_JSON } from "../utils/config";
import fallbackSchema from "../../../generated/bus-intercept.json";

interface SwaggerSchema {
  components: {
    schemas: Record<string, any>;
  };
}

type WisrEventsMap = Record<string, Record<string, any>>;

interface SchemaContextType {
  schema: SwaggerSchema | null;
  wisrEvents: WisrEventsMap;
  loading: boolean;
  error: Error | null;
  schemaSource: "live" | "static" | null;
}

const SchemaContext = createContext<SchemaContextType | undefined>(undefined);

function buildWisrEventsMap(schema: SwaggerSchema): WisrEventsMap {
  const schemas = schema.components.schemas;
  const map: WisrEventsMap = {};

  for (const [key, value] of Object.entries(schemas)) {
    if (!value?.properties) continue;

    const resolved: Record<string, any> = {};
    for (const [prop, propSchema] of Object.entries<any>(value.properties)) {
      if (propSchema?.$ref) {
        const refName = propSchema.$ref.replace("#/components/schemas/", "");
        resolved[prop] = schemas[refName] ?? propSchema;
      } else {
        resolved[prop] = propSchema;
      }
    }
    map[key] = resolved;
  }

  return map;
}

export function SchemaProvider({ children }: { children: ReactNode }) {
  const [schema, setSchema] = useState<SwaggerSchema | null>(null);
  const [wisrEvents, setWisrEvents] = useState<WisrEventsMap>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [schemaSource, setSchemaSource] = useState<"live" | "static" | null>(null);

  const hasFetched = useRef(false);

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;

    const fetchSchema = async () => {
      try {
        console.log("Fetching schema from:", SWAGGER_JSON);
        const { data } = await axios.get<SwaggerSchema>(SWAGGER_JSON);
        setSchema(data);
        setWisrEvents(buildWisrEventsMap(data));
        setSchemaSource("live");
        console.log("Schema loaded from API");
        toast.success("Events loaded from live API — you're on the latest version", {
          position: "bottom-right",
        });
      } catch (err) {
        console.warn("API schema fetch failed, using static fallback:", err);
        try {
          const data = fallbackSchema as unknown as SwaggerSchema;
          setSchema(data);
          setWisrEvents(buildWisrEventsMap(data));
          setSchemaSource("static");
          console.log("Schema loaded from static fallback");
          toast.warn(
            "API unreachable — loaded events from static fallback. Check console for details.",
            {
              position: "bottom-right",
              autoClose: false,
            }
          );
        } catch (fallbackErr) {
          const errorMsg =
            fallbackErr instanceof Error
              ? fallbackErr.message
              : "Failed to load schema from both API and static fallback";
          setError(new Error(errorMsg));
          toast.error(
            "Failed to load events from API and static fallback. Check console for details.",
            {
              position: "bottom-right",
              autoClose: false,
            }
          );
        }
      } finally {
        setLoading(false);
      }
    };

    fetchSchema();
  }, []);

  return (
    <SchemaContext.Provider value={{ schema, wisrEvents, loading, error, schemaSource }}>
      {children}
    </SchemaContext.Provider>
  );
}

export function useSchema(): SchemaContextType {
  const context = useContext(SchemaContext);
  if (!context) {
    throw new Error("useSchema must be used within a SchemaProvider");
  }
  return context;
}
